import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage pinned and most-used asset types per project
 * Stores data in localStorage with structure: { [projectCode]: { pinned: [], usage: {} } }
 * 
 * @param {string} projectCode - The project code/identifier
 * @param {number} maxPinned - Maximum number of pinned items to keep (default: 5)
 * @returns {Object} Object with pinned assets, usage stats, and management functions
 */
export const usePinnedAssetTypes = (projectCode, maxPinned = 5) => {
  const [pinnedAssets, setPinnedAssets] = useState([]);
  const [usageStats, setUsageStats] = useState({});
  const [mostUsed, setMostUsed] = useState([]);

  const storageKey = 'iot_portal_pinned_asset_types';

  // Initialize from localStorage
  useEffect(() => {
    if (!projectCode) return;

    try {
      const stored = localStorage.getItem(storageKey);
      const data = stored ? JSON.parse(stored) : {};
      const projectData = data[projectCode] || { pinned: [], usage: {} };

      setPinnedAssets(projectData.pinned || []);
      setUsageStats(projectData.usage || {});

      // Calculate most used
      const sorted = Object.entries(projectData.usage || {})
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, maxPinned)
        .map(([type]) => type);
      setMostUsed(sorted);
    } catch (error) {
      console.error('Error loading pinned asset types:', error);
    }
  }, [projectCode, maxPinned]);

  // Save to localStorage
  const saveToStorage = useCallback((pinned, usage) => {
    if (!projectCode) return;

    try {
      const stored = localStorage.getItem(storageKey);
      const data = stored ? JSON.parse(stored) : {};
      data[projectCode] = { pinned, usage };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving pinned asset types:', error);
    }
  }, [projectCode]);

  // Toggle pin status
  const togglePin = useCallback((assetType) => {
    setPinnedAssets((prev) => {
      const updated = prev.includes(assetType)
        ? prev.filter((type) => type !== assetType)
        : [...prev, assetType].slice(-maxPinned); // Keep only max pinned
      saveToStorage(updated, usageStats);
      return updated;
    });
  }, [usageStats, maxPinned, saveToStorage]);

  // Track usage
  const trackUsage = useCallback((assetType) => {
    setUsageStats((prev) => {
      const updated = {
        ...prev,
        [assetType]: (prev[assetType] || 0) + 1,
      };
      saveToStorage(pinnedAssets, updated);

      // Update most used
      const sorted = Object.entries(updated)
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, maxPinned)
        .map(([type]) => type);
      setMostUsed(sorted);

      return updated;
    });
  }, [pinnedAssets, maxPinned, saveToStorage]);

  // Check if asset type is pinned
  const isPinned = useCallback((assetType) => pinnedAssets.includes(assetType), [pinnedAssets]);

  // Get pinned items with usage count
  const getPinnedWithStats = useCallback(() => {
    return pinnedAssets.map((type) => ({
      type,
      usage: usageStats[type] || 0,
    }));
  }, [pinnedAssets, usageStats]);

  // Clear all data for project
  const clearAll = useCallback(() => {
    setPinnedAssets([]);
    setUsageStats({});
    setMostUsed([]);
    saveToStorage([], {});
  }, [saveToStorage]);

  return {
    pinnedAssets,
    usageStats,
    mostUsed,
    togglePin,
    trackUsage,
    isPinned,
    getPinnedWithStats,
    clearAll,
  };
};

export default usePinnedAssetTypes;
